import {ErrorCode} from '@rambler-tech/video-ad-sdk'
import {type Size, PlayerError} from '@clappr/core'
import {loadScript} from 'utils/dom'
import {Events} from 'common/types/events'
import {
  type AdvertisementSource,
  type AdvertisementYandexSource,
  type AdvertisementAdfoxSource,
  AdvertisementProvider,
  AdvertisementType,
  AdvertisementBlockType
} from 'common/api/records'
import {UNKNOWN_ID} from 'common/constants'
import {AdPlayback, type AdSourceOptions} from '../ad-playback'

export interface AdPodError extends Error {
  code?: string
}

export class YandexPlayback<
  T extends
    | AdvertisementYandexSource
    | AdvertisementAdfoxSource = AdvertisementYandexSource
> extends AdPlayback<T> {
  public static PROGRESS_INTERVAL = 500

  private static IMPID: Record<string, number> = {
    [AdvertisementBlockType.PRE_ROLL]: 1,
    [AdvertisementBlockType.POST_ROLL]: 2,
    [AdvertisementBlockType.MIDDLE_ROLL]: 3,
    [AdvertisementBlockType.PAUSE_ROLL]: 3,
    [AdvertisementBlockType.BANNER]: 4,
    [AdvertisementBlockType.PAUSE_BANNER]: 3,
    [AdvertisementBlockType.PSEUDO_STREAMING]: 3
  }

  private static preloadPromise?: Promise<any>

  public static canPlay(source: AdvertisementSource): boolean {
    return (
      !!window.ya?.videoAd &&
      source.provider === AdvertisementProvider.YANDEX &&
      !!source.partnerId
    )
  }

  public static async preload(): Promise<void> {
    if (window.ya?.videoAd) return

    YandexPlayback.preloadPromise ??= (async () => {
      try {
        await loadScript('https://yandex.ru/ads/system/adsdk.js')
      } catch {
        delete YandexPlayback.preloadPromise
      }
    })()

    return await YandexPlayback.preloadPromise
  }

  // NOTE: мапинг ошибок yandex ad sdk к стандартным кодам ошибок vast/vpaid
  //       https://yandex.ru/dev/video-sdk/doc/dg/sdk-html5/Error-interface.html#list
  private static ErrorCode: Record<string, number> = {
    NO_AD_SECTION: ErrorCode.VAST_NO_ADS_AFTER_WRAPPER,
    VIDEO_PLAY_REJECTED: ErrorCode.VIDEO_PLAY_ERROR,
    VPAID_METHOD_CALL_ERROR: ErrorCode.VPAID_ERROR,
    YANDEX_VPAID_METHOD_CALL_ERROR: ErrorCode.VPAID_ERROR,
    YANDEX_VPAID_FRIENDLY_IFRAME_SECURITY_POLICY_VIOLATION:
      ErrorCode.VPAID_ERROR,
    SLOT_SMALL_SIZE: ErrorCode.VAST_UNEXPECTED_MEDIA_FILE,
    VPAID_START_TIMEOUT: ErrorCode.VAST_MEDIA_LOAD_TIMEOUT,
    VPAIDPlayingError: ErrorCode.VIDEO_PLAY_ERROR,
    VMAP_LOAD_TIMEOUT: ErrorCode.VAST_LOAD_TIMEOUT,
    MEDIA_ERR_SRC_NOT_SUPPORTED: ErrorCode.VAST_LINEAR_ASSET_MISMATCH,
    VPAIDLoadingError: ErrorCode.VAST_MEDIA_LOAD_TIMEOUT,
    WRAPPER_LOAD_TIMEOUT: ErrorCode.VAST_LOAD_TIMEOUT,
    MEDIA_ERR_DECODE_ON_START: ErrorCode.VAST_PROBLEM_DISPLAYING_MEDIA_FILE,
    WRAPPER_LOAD_ERROR: ErrorCode.VAST_WRAPPER_ERROR,
    YANDEX_VAST_LOAD_TIMEOUT: ErrorCode.VAST_LOAD_TIMEOUT,
    YANDEX_WRAPPER_LOAD_ERROR: ErrorCode.VAST_WRAPPER_ERROR,
    YANDEX_WRAPPER_LOAD_TIMEOUT: ErrorCode.VAST_LOAD_TIMEOUT,
    VIDEO_TIMEOUT: ErrorCode.VAST_MEDIA_LOAD_TIMEOUT,
    YANDEX_NO_GET_VPAID_AD_FUNC: ErrorCode.VPAID_ERROR,
    YANDEX_WRAPPER_MAX_COUNT_LIMIT: ErrorCode.VAST_TOO_MANY_REDIRECTS,
    YANDEX_VPAID_FRIENDLY_IFRAME_UNHANDLED_GLOBAL_ERROR: ErrorCode.VPAID_ERROR,
    MSE_VIDEO_ATTACH_FAIL: ErrorCode.VIDEO_PLAY_ERROR
  }

  private adViewer?: any
  private adPlaybackController?: any
  private adPodError?: AdPodError
  private adProgressInterval?: number
  private diffStartTime?: number
  private currentTime = 0

  public allowBlock = false
  public reloadFailedAdTag = false

  public get name(): string {
    return 'yandexPlayback'
  }

  public async createLoader(sources: T): Promise<any> {
    const {AdLoader} = await window.ya.videoAd.loadModule('AdLoader')
    const {partnerId, category, impId} = sources as AdvertisementYandexSource

    return await AdLoader.create({partnerId, category, impId})
  }

  public async load(sources: T): Promise<void> {
    this.sources = sources

    try {
      const adLoader = await this.createLoader(sources)

      this.adViewer = await adLoader.loadAd()

      const {containerElement, videoElement} = this.options

      this.adPlaybackController = this.adViewer.createPlaybackController(
        videoElement,
        containerElement
      )
      this.adPlaybackController.subscribe('AdStarted', this.onAdStarted)
      this.adPlaybackController.subscribe(
        'AdVolumeChange',
        this.onAdVolumeChange
      )
      this.adPlaybackController.subscribe('AdClickThru', this.onAdClick)
      this.adPlaybackController.subscribe('AdPodSkipped', this.onAdSkipped)
      this.adPlaybackController.subscribe('AdPodError', this.onAdPodError)
      this.adPlaybackController.subscribe('AdStopped', this.onAdStopped)
      // trigger loaded to show ad container before the start ad
      this.trigger(Events.ADPLAYBACK_LOADED, this.sources)
      this.trigger(Events.ADPLAYBACK_READY, this.sources)
    } catch (error: any) {
      this.onAdError(error)
    }
  }

  public static getAdPlaybackSources(
    source: AdvertisementSource,
    options: AdSourceOptions
  ): AdvertisementSource {
    const {blockType} = options

    if (source.provider === AdvertisementProvider.YANDEX) {
      if ('partnerId' in source) {
        source.partnerId = Number(source.partnerId)
      }

      if ('category' in source) {
        source.category = Number(source.category)
      }

      source.impId = Number(source.impId) || YandexPlayback.IMPID[blockType]
    }

    return super.getAdPlaybackSources(source, options)
  }

  private onAdStarted = (): void => {
    // NOTE: send impression on started, because yandex sdk triggers impression after start
    this.trigger(Events.ADPLAYBACK_IMPRESSION, this.sources)
    this.trigger(Events.ADPLAYBACK_STARTED, this.sources)
    this.onAdProgress()
    this.adProgressInterval = window.setInterval(
      this.onAdProgress,
      YandexPlayback.PROGRESS_INTERVAL
    )
  }

  protected onAdProgress = (): void => {
    const isPlaying = this.adPlaybackController.getAdPlayingState() === 'play'

    if (!isPlaying) {
      delete this.diffStartTime

      return
    }

    const duration = this.getDuration()
    const diffStartTime = Date.now() / 1000
    const diff = diffStartTime - (this.diffStartTime ?? diffStartTime)
    const currentTime =
      this.currentTime < 0 ? 0 : Math.min(this.currentTime + diff, duration)

    this.diffStartTime = diffStartTime

    if (currentTime === this.currentTime) {
      return
    }

    this.currentTime = currentTime
    this.trigger(Events.ADPLAYBACK_PROGRESS, {
      ...this.sources,
      current: currentTime,
      total: duration
    })
  }

  protected onAdVolumeChange = (): void => {
    this.trigger(Events.ADPLAYBACK_VOLUME_CHANGE, this.sources)
  }

  protected onAdClick = (): void => {
    this.trigger(Events.ADPLAYBACK_CLICK, this.sources)
  }

  protected onAdSkipped = (): void => {
    this.trigger(Events.ADPLAYBACK_SKIPPED, this.sources)
  }

  private onAdPodError = (error: AdPodError): void => {
    this.adPodError = {...error}
  }

  private onAdStopped = (): void => {
    const isBrokenConfig =
      this.adPodError?.code === 'VPAIDLoadingError' ||
      this.adPodError?.code === 'NO_AD_SECTION'

    if (this.adPodError && isBrokenConfig) {
      this.onAdError(this.adPodError)

      return
    }

    this.onAdContentResumeRequested()
  }

  private onAdContentResumeRequested = (): void => {
    this.trigger(Events.ADPLAYBACK_CONTENT_RESUME_REQUESTED, this.sources)
  }

  private onAdError = (error: AdPodError): void => {
    const errorCode = error.code && YandexPlayback.ErrorCode[error.code]

    const formattedError = this.createError({
      code: `${errorCode ?? UNKNOWN_ID}`,
      description: error.message,
      level: PlayerError.Levels.WARN,
      raw: {...this.sources, code: errorCode}
    })

    this.trigger(Events.ADPLAYBACK_ERROR, formattedError)
  }

  public start(): void {
    this.adPlaybackController?.playAd()
  }

  public pause(): void {
    this.adPlaybackController?.pauseAd()
    // yandex sdk don't trigger paused event
    this.trigger(Events.ADPLAYBACK_PAUSED, this.sources)
  }

  public resume(): void {
    this.adPlaybackController?.resumeAd()
    // yandex sdk don't trigger resumed event
    this.trigger(Events.ADPLAYBACK_RESUMED, this.sources)
  }

  public skip(): void {
    this.adPlaybackController?.skipAd()
  }

  public setVolume(volume: number): void {
    this.adPlaybackController?.setAdVolume(volume / 100)
  }

  public getVolume(): number {
    const volume = this.adPlaybackController?.getAdVolume() ?? 0

    return volume * 100
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public resize(_size?: Size): void {}

  public getDuration(): number {
    return this.adPlaybackController?.getAdDuration()
  }

  public getCurrentTime(): number {
    return this.currentTime
  }

  public isLinear(): boolean {
    return true
  }

  public getAdSystem(): string {
    return 'Yandex'
  }

  public getAdType(): AdvertisementType {
    return AdvertisementType.UNKNOWN
  }

  public destroy(): void {
    clearInterval(this.adProgressInterval)
    this.adViewer?.destroy()
  }
}
